import React, { useState } from 'react';
import { MessageSquarePlus, Send, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { API_ROOT } from '@/config';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

interface FeatureRequestButtonProps {
  className?: string;
  style?: React.CSSProperties;
}

const FeatureRequestButton: React.FC<FeatureRequestButtonProps> = ({ 
  className = '', 
  style = {} 
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [requestText, setRequestText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sendError, setSendError] = useState('');

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setRequestText('');
    setSendSuccess(false);
    setSendError('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRequestText('');
    setSendSuccess(false);
    setSendError('');
    setIsSending(false);
  };

  const handleSendRequest = async () => {
    if (!requestText.trim()) {
      setSendError('Please enter your feature request');
      return;
    }

    setIsSending(true);
    setSendError('');

    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        setSendError('You must be logged in to send a feature request');
        setIsSending(false);
        return;
      }

      const response = await fetch(`${API_ROOT}/send-feature-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: requestText.trim()
        }),
      });

      if (response.ok) {
        setSendSuccess(true);
        setRequestText('');
        setTimeout(() => {
          handleCloseModal();
        }, 2000);
      } else {
        setSendError('Failed to send feature request. Please try again.');
      }
    } catch (error) {
      setSendError('Network error. Please check your connection and try again.');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <Button
        onClick={handleOpenModal}
        variant="outline"
        size="sm"
        className={`flex items-center gap-2 hover:shadow-sm transition-all duration-200 ${className}`}
        style={{
          borderColor: PALETTE.paleViolet,
          color: PALETTE.gunmetal,
          backgroundColor: 'transparent',
          borderRadius: '24px', // Round edges
          ...style
        }}
        title="Submit a feature request"
      >
        <MessageSquarePlus className="h-4 w-4" />
        <span className="hidden sm:inline">Feature Request</span>
      </Button>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent 
          className="sm:max-w-[500px] p-0"
          style={{ 
            borderRadius: '16px',
            border: `2px solid ${PALETTE.paleViolet}`,
            backgroundColor: PALETTE.brightSnow 
          }}
        >
          <DialogHeader className="p-6 pb-4">
            <DialogTitle 
              className="text-xl font-bold flex items-center gap-2"
              style={{ color: PALETTE.gunmetal }}
            >
              <MessageSquarePlus className="h-5 w-5" style={{ color: PALETTE.paleViolet }} />
              Feature Request
            </DialogTitle>
          </DialogHeader>
          {/* Description region for accessibility */}
          <div id="feature-request-desc" className="sr-only">Submit a feature request to the BrandComply team.</div>
          <div className="px-6 pb-6" aria-describedby="feature-request-desc">
            {sendSuccess ? (
              <div className="text-center py-8">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${PALETTE.inchworm}20`, color: PALETTE.inchworm }}
                >
                  <Send className="h-8 w-8" />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: PALETTE.gunmetal }}>
                  Thank you!
                </h3>
                <p style={{ color: PALETTE.gunmetal, opacity: 0.8 }}>
                  Your feature request has been sent successfully.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label 
                    className="block text-sm font-medium mb-2"
                    style={{ color: PALETTE.gunmetal }}
                  >
                    What feature would you like to see in BrandComply?
                  </label>
                  <Textarea
                    value={requestText}
                    onChange={(e) => setRequestText(e.target.value)}
                    placeholder="Please describe the feature you'd like to request and how it would help you..."
                    rows={6}
                    className="w-full resize-none"
                    style={{
                      borderColor: PALETTE.americanSilver,
                      borderRadius: '12px',
                      fontSize: '14px',
                      lineHeight: '1.5'
                    }}
                    disabled={isSending}
                  />
                </div>
                
                {sendError && (
                  <div 
                    className="p-3 rounded-lg text-sm"
                    style={{ 
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      border: '1px solid #fecaca'
                    }}
                  >
                    {sendError}
                  </div>
                )}
                
                <div className="flex justify-end gap-3">
                  <Button
                    onClick={handleCloseModal}
                    variant="outline"
                    disabled={isSending}
                    style={{
                      borderColor: PALETTE.americanSilver,
                      color: PALETTE.gunmetal,
                      borderRadius: '8px'
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSendRequest}
                    disabled={isSending || !requestText.trim()}
                    className="flex items-center gap-2"
                    style={{
                      backgroundColor: PALETTE.paleViolet,
                      color: PALETTE.brightSnow,
                      borderRadius: '8px'
                    }}
                  >
                    {isSending ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Send
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeatureRequestButton; 